import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios, { AxiosError, AxiosHeaders } from 'axios'

import type { AuthResponse } from '@/api/v1/auth/login'
import { FRONTEND_VERSION } from '@/lib/frontend-version/frontend-version'
import { logger } from '@/lib/logger'
import { useAuthStore } from '@/stores/auth'
import { useContextStore } from '@/stores/context'
import { useManagedAccountStore } from '@/stores/managed-account'
import { useSandboxStore } from '@/stores/sandbox'

import {
  AUTH_LOGIN_ENDPOINT,
  AUTH_REFRESH_ENDPOINT,
  HEADER_APP_VERSION,
  HEADER_CAPTCHA_BYPASS,
  HEADER_MANAGED_ACCOUNT_ID,
  HEADER_SANDBOX_MODE,
  HEADER_TELEMETRY_OPERATION_ID,
  HEADER_USER_ACCOUNT_ID,
} from './api-client.consts'
import type { TokenRefreshResult } from './api-client.types'
import { redactSensitive } from './redact-sensitive'

const API_URL = import.meta.env.VITE_API_URL

export class FileReadError extends Error {
  constructor(cause: unknown) {
    super('Could not read file before upload', { cause })
    this.name = 'FileReadError'
  }
}

/**
 * Checks if the request targets an external URL (not the Signater API).
 * When axios receives a full URL (e.g. https://blob.storage.com/...), it ignores baseURL.
 * We detect this to skip injecting Signater-specific headers into external requests.
 */
export const isExternalRequest = (
  config: InternalAxiosRequestConfig,
): boolean => {
  const url = config.url || ''

  // Relative URLs (e.g. /v1/ecm/documents) always go to the API
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false
  }

  // Full URL that starts with our API base → internal
  if (API_URL && url.startsWith(API_URL)) {
    return false
  }

  return true
}

const getErrorName = (error: unknown): string =>
  error instanceof Error || error instanceof DOMException
    ? error.name
    : 'UnknownError'

const copyFileIntoMemory = async (
  file: File,
  config: InternalAxiosRequestConfig,
): Promise<File> => {
  try {
    const bytes = await file.arrayBuffer()
    return new File([bytes], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    })
  } catch (cause) {
    const error = new FileReadError(cause)
    logger.error(
      'File read failed before upload',
      error,
      {
        method: config.metadata?.method,
        url: config.metadata?.url,
        errorName: getErrorName(cause),
        fileSize: file.size,
        fileType: file.type,
      },
      'api',
    )
    throw error
  }
}

export const materializeFormDataFiles = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  if (!(config.data instanceof FormData)) {
    return config
  }

  const materialized = new FormData()
  for (const [key, value] of config.data.entries()) {
    materialized.append(
      key,
      typeof value === 'string'
        ? value
        : await copyFileIntoMemory(value, config),
    )
  }

  config.data = materialized
  return config
}

/**
 * Adds request metadata for tracking and logging purposes
 */
export const addRequestMetadata = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  config.metadata = {
    startTime: performance.now(),
    url: config.url || '',
    method: (config.method || 'unknown').toUpperCase(),
  }
  return config
}

// Dev-only escape hatch. Cloudflare Turnstile won't issue a token in an
// embedded or automated browser (Orca's webview, Playwright), so every
// captcha-gated endpoint — login, register, password recovery — fails
// verification locally. Non-prod backends accept this shared token instead;
// it is the same gate tests/e2e/helpers/test-helpers.ts uses.
// Vite replaces import.meta.env.DEV with false when building for production,
// so this branch and the token literal are dropped from that bundle.
// Callers are internal-API only (addCustomHeaders runs behind
// !isExternalRequest), so the token never reaches a third party.
export const captchaBypassHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {}

  if (import.meta.env.DEV) {
    const captchaBypassToken = import.meta.env.VITE_CAPTCHA_BYPASS_TOKEN
    if (captchaBypassToken) {
      headers[HEADER_CAPTCHA_BYPASS] = captchaBypassToken
    }
  }

  return headers
}

/**
 * Collects all Signater-specific headers (auth + custom) from the current store state.
 * Single source of truth used by both `buildApiHeaders` (for native fetch)
 * and `addAuthHeaders`/`addCustomHeaders` (for axios interceptors).
 */
const getSignaterHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {}

  headers[HEADER_APP_VERSION] = FRONTEND_VERSION

  const { accessToken } = useAuthStore.getState()
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`
  }

  const { selectedUserAccountId } = useContextStore.getState()
  if (selectedUserAccountId) {
    headers[HEADER_USER_ACCOUNT_ID] = selectedUserAccountId
  }

  const { isSandboxMode } = useSandboxStore.getState()
  headers[HEADER_SANDBOX_MODE] = isSandboxMode ? 'true' : 'false'

  const { isManaging, accountId: managedAccountId } =
    useManagedAccountStore.getState()
  if (isManaging && managedAccountId) {
    headers[HEADER_MANAGED_ACCOUNT_ID] = managedAccountId
  }

  return { ...headers, ...captchaBypassHeaders() }
}

/**
 * Builds a plain headers object with auth + custom Signater headers.
 * Used by native fetch calls that need the same headers as apiClient
 * but without going through axios interceptors.
 */
export const buildApiHeaders = (): Record<string, string> => {
  return getSignaterHeaders()
}

/**
 * Adds authentication headers to the request
 */
export const addAuthHeaders = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const { accessToken } = useAuthStore.getState()

  if (accessToken) {
    config.headers.authorization = `Bearer ${accessToken}`
  }

  return config
}

/**
 * Adds custom Signater headers (UserAccountId, SandboxMode, and ManagedAccountId)
 */
export const addCustomHeaders = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const headers = getSignaterHeaders()

  for (const [key, value] of Object.entries(headers)) {
    // Skip authorization — handled separately by addAuthHeaders
    if (key === 'authorization') continue
    config.headers[key] = value
  }

  return config
}

/**
 * Logs the request start with relevant metadata
 */
export const logRequestStart = (config: InternalAxiosRequestConfig): void => {
  const sent = AxiosHeaders.from(config.headers)

  logger.debug(
    'API request started',
    {
      method: config.metadata?.method,
      url: config.metadata?.url,
      baseURL: config.baseURL,
      hasAuth: sent.has('authorization'),
      userAccountId: sent.get(HEADER_USER_ACCOUNT_ID) ?? null,
      isSandbox: sent.get(HEADER_SANDBOX_MODE) === 'true',
      payload: redactSensitive(config.data),
      params: config.params,
    },
    'api',
  )
}

/**
 * Calculates the duration of the request
 */
export const calculateRequestDuration = (
  config: InternalAxiosRequestConfig,
): number => {
  return config.metadata ? performance.now() - config.metadata.startTime : 0
}

/**
 * Extracts telemetry operation ID from response headers
 */
export const extractTelemetryId = (
  response: AxiosResponse | AxiosError['response'],
): string | undefined => {
  return response?.headers[HEADER_TELEMETRY_OPERATION_ID]
}

/**
 * Logs successful API response
 */
export const logSuccessfulResponse = (
  response: AxiosResponse,
  duration: number,
  telemetryOperationId: string | undefined,
): void => {
  logger.info(
    'API request completed',
    {
      method:
        response.config.metadata?.method ||
        response.config.method?.toUpperCase(),
      url: response.config.metadata?.url || response.config.url,
      status: response.status,
      duration: `${duration.toFixed(2)}ms`,
      telemetryOperationId,
      responseSize: JSON.stringify(response.data).length,
      payload: redactSensitive(response.config.data),
      params: response.config.params,
      response: redactSensitive(response.data),
    },
    'api',
  )
}

/**
 * Logs failed API request
 */
export const logFailedRequest = (
  error: AxiosError,
  duration: number,
  telemetryOperationId: string | undefined,
): void => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean
  }

  logger.error(
    'API request failed',
    error,
    {
      method:
        originalRequest.metadata?.method ||
        originalRequest.method?.toUpperCase(),
      url: originalRequest.metadata?.url || originalRequest.url,
      status: error.response?.status,
      duration: `${duration.toFixed(2)}ms`,
      telemetryOperationId,
      errorCode: (error.response?.data as any)?.code,
      errorMessage: (error.response?.data as any)?.message || error.message,
      isRetry: originalRequest._retry,
    },
    'api',
  )
}

export const MALFORMED_RESPONSE_MESSAGE = 'API response body is not valid JSON'

export const isMalformedResponse = (response: AxiosResponse): boolean => {
  const { config, data } = response

  if (isExternalRequest(config)) {
    return false
  }

  if (config.responseType && config.responseType !== 'json') {
    return false
  }

  if (typeof data !== 'string' || data.length === 0) {
    return false
  }

  const contentType = String(response.headers['content-type'] ?? '')
  return !contentType.startsWith('text/plain')
}

export const createMalformedResponseError = (
  response: AxiosResponse,
): AxiosError =>
  new AxiosError(
    MALFORMED_RESPONSE_MESSAGE,
    AxiosError.ERR_BAD_RESPONSE,
    response.config,
    response.request,
    response,
  )

/**
 * Checks if the request is an auth endpoint (login or refresh)
 */
export const isAuthEndpoint = (url?: string): boolean => {
  return !!(
    url?.includes(AUTH_LOGIN_ENDPOINT) || url?.includes(AUTH_REFRESH_ENDPOINT)
  )
}

/**
 * Silently clears auth state without redirecting.
 * Used for public endpoints where the request can be retried anonymously.
 */
export const handleSilentSignout = (
  url: string | undefined,
  telemetryOperationId: string | undefined,
): void => {
  logger.info(
    'Silent signout - clearing auth and retrying without token',
    {
      endpoint: url,
      telemetryOperationId,
    },
    'auth',
  )
  useAuthStore.getState().clearAuth()
}

/**
 * Handles authentication failure by clearing auth state
 */
export const handleAuthFailure = (
  url: string | undefined,
  telemetryOperationId: string | undefined,
): void => {
  logger.info(
    'Authentication failed - clearing auth state',
    {
      endpoint: url,
      telemetryOperationId,
    },
    'auth',
  )
  useAuthStore.getState().clearAuth()
}

/**
 * Performs token refresh
 */
export const performTokenRefresh = async (
  refreshToken: string,
): Promise<TokenRefreshResult> => {
  const response = await axios.post<AuthResponse>(
    `${API_URL}${AUTH_REFRESH_ENDPOINT}`,
    { refreshToken },
    {
      timeout: 15_000,
      headers: {
        ['Content-Type']: 'application/json',
      },
    },
  )

  const telemetryId = extractTelemetryId(response)

  return {
    auth: response.data,
    telemetryId,
  }
}

const carriesBusinessRule = (error: AxiosError): boolean => {
  const data: unknown = error.response?.data
  return typeof data === 'object' && data !== null && 'errors' in data
}

/**
 * Handles 403 forbidden errors when in sandbox mode.
 *
 * When the account-level sandbox feature is disabled but the user is still
 * in sandbox mode (sending X-Signater-Sandbox-Mode: true), the API returns 403.
 *
 * This function:
 * 1. Detects if we're in sandbox mode and got a 403
 * 2. Disables sandbox mode
 * 3. Retries the original request without the sandbox header
 *
 * Returns true if the error was handled and the request should be retried,
 * false if the error should be propagated.
 */
export const handle403SandboxError = async (
  apiClient: any,
  error: AxiosError,
  telemetryOperationId: string | undefined,
): Promise<{ handled: boolean; response?: AxiosResponse }> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retrySandbox?: boolean
  }

  // Avoid infinite loops
  if (originalRequest._retrySandbox) {
    return { handled: false }
  }

  const { isSandboxMode } = useSandboxStore.getState()

  // Only handle if we're in sandbox mode
  if (!isSandboxMode) {
    return { handled: false }
  }

  if (carriesBusinessRule(error)) {
    return { handled: false }
  }

  logger.info(
    'Received 403 while in sandbox mode - disabling sandbox and retrying',
    {
      url: originalRequest.url,
      telemetryOperationId,
    },
    'api',
  )

  // Disable sandbox mode
  useSandboxStore.getState().setIsSandboxMode(false)

  // Mark request to avoid infinite loops
  originalRequest._retrySandbox = true

  // Update the header for the retry
  originalRequest.headers[HEADER_SANDBOX_MODE] = 'false'

  try {
    const response = await apiClient(originalRequest)
    return { handled: true, response }
  } catch (retryError) {
    // If retry also fails, propagate the error
    return { handled: false }
  }
}
